import { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-stone-900 mb-3">
            Privacy Policy
          </h1>
          <p className="text-stone-500 text-sm font-sans mb-12">
            Last updated: May 31, 2026
          </p>

          <div className="flex flex-col gap-8 text-stone-700 font-sans text-sm leading-relaxed">
            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                1. Introduction
              </h2>
              <p>
                Lithos ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal information when you visit our website or use our services. By using our website, you consent to the practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                2. Information We Collect
              </h2>
              <p className="mb-3">
                We may collect the following types of information:
              </p>
              <ul className="list-disc list-inside flex flex-col gap-2 ml-2">
                <li>
                  <strong className="text-stone-900">Contact Information:</strong> Name, company name, email address, phone number, and business address provided through our contact forms or quotation requests.
                </li>
                <li>
                  <strong className="text-stone-900">Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and browser type.
                </li>
                <li>
                  <strong className="text-stone-900">Technical Data:</strong> IP address, device information, and cookies used to improve your browsing experience.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                3. How We Use Your Information
              </h2>
              <p className="mb-3">
                We use your personal information for the following purposes:
              </p>
              <ul className="list-disc list-inside flex flex-col gap-2 ml-2">
                <li>To respond to your inquiries and provide quotations</li>
                <li>To process and manage your orders</li>
                <li>To communicate updates about our products and services</li>
                <li>To improve our website and customer experience</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                4. Data Sharing and Disclosure
              </h2>
              <p className="mb-3">
                We do not sell, rent, or trade your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside flex flex-col gap-2 ml-2">
                <li>With trusted service providers who assist us in operating our business (e.g., shipping partners, logistics providers)</li>
                <li>When required by law or to protect our legal rights</li>
                <li>In connection with a business transfer, merger, or acquisition</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                5. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security of your data.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                6. Your Rights
              </h2>
              <p className="mb-3">
                Under applicable data protection laws, you have the right to:
              </p>
              <ul className="list-disc list-inside flex flex-col gap-2 ml-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to the processing of your information</li>
                <li>Request restriction of processing</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:contact@lithos.pro" className="text-gold-600 hover:underline">
                  contact@lithos.pro
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                7. Cookies
              </h2>
              <p>
                Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device that help us analyze website traffic and improve functionality. You can disable cookies through your browser settings, though this may affect certain features of the website.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                8. Changes to This Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-stone-900 mb-3">
                9. Contact Us
              </h2>
              <p>
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at{" "}
                <a href="mailto:contact@lithos.pro" className="text-gold-600 hover:underline">
                  contact@lithos.pro
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}